// The per-frame budget of web-rpg-engine-stack §5 (main + shadow passes), as numbers the perf tour checks.
// Every tier is checked against both columns; the tier's own column (`target`) decides its pass/fail:
// low → mobile (855-class, 30 fps), mid and high → desktop (mid GPU, 1080p, 60 fps). §5 has no mid column.

import type { QualityTier } from "@rpg/look";

/** The §5 budget's two columns. */
export type Column = "desktop" | "mobile";

/** Per-frame limits per column. */
export const BUDGET: Record<Column, { drawCalls: number; triangles: number; textureMB: number; shadowMapSize: number; pixelRatio: number; postPasses: number; frameMs: number }> = {
  desktop: { drawCalls: 300, triangles: 1_500_000, textureMB: 256, shadowMapSize: 2048, pixelRatio: 2, postPasses: 5, frameMs: 1000 / 60 },
  mobile: { drawCalls: 120, triangles: 400_000, textureMB: 96, shadowMapSize: 1024, pixelRatio: 1.5, postPasses: 1, frameMs: 1000 / 30 },
};

/** The column a tier is judged against. */
export const targetColumn = (tier: QualityTier): Column => (tier === "low" ? "mobile" : "desktop");

export interface BudgetLine {
  item: string;
  value: number;
  desktop: { limit: number; pass: boolean };
  mobile: { limit: number; pass: boolean };
  /** Not judged (frame times on software GL): reported, never fails the tier. */
  informational?: boolean;
  note?: string;
}

export interface BudgetInput {
  tier: QualityTier;
  drawCallsMax: number;
  trianglesMax: number;
  textureMBEst: number;
  shadowMapSize: number;
  pixelRatio: number;
  postPasses: number;
  frameMsP95: number;
  gpuMsP95: number | null;
  /** Software GL (SwiftShader, llvmpipe): frame times say nothing about a real GPU. */
  softwareGL: boolean;
}

/** Every line against both columns; pass/fail on the tier's own column, with a text table. */
export function checkBudget(b: BudgetInput): { target: Column; pass: boolean; lines: BudgetLine[]; text: string[] } {
  const line = (item: string, value: number, key: keyof (typeof BUDGET)["desktop"], extra: Partial<BudgetLine> = {}): BudgetLine => ({
    item,
    value,
    desktop: { limit: round(BUDGET.desktop[key]), pass: value <= BUDGET.desktop[key] },
    mobile: { limit: round(BUDGET.mobile[key]), pass: value <= BUDGET.mobile[key] },
    ...extra,
  });
  const timing = b.softwareGL ? { informational: true, note: "software GL: frame time is meaningless here" } : {};
  const lines: BudgetLine[] = [
    line("drawCalls (max frame)", b.drawCallsMax, "drawCalls"),
    line("triangles (max frame)", b.trianglesMax, "triangles"),
    line("textureMB (estimate)", round(b.textureMBEst), "textureMB", { note: "scene textures + render targets + canvas, estimated from sizes" }),
    line("shadowMapSize", b.shadowMapSize, "shadowMapSize"),
    line("pixelRatio", round(b.pixelRatio), "pixelRatio"),
    line("postPasses (full-screen, after the scene)", b.postPasses, "postPasses"),
    line("frameMs p95", round(b.frameMsP95), "frameMs", timing),
  ];
  if (b.gpuMsP95 !== null) lines.push(line("gpuMs p95 (timer query)", round(b.gpuMsP95), "frameMs", timing));
  const target = targetColumn(b.tier);
  const pass = lines.every((l) => l.informational || l[target].pass);
  const mark = (ok: boolean): string => (ok ? "PASS" : "FAIL");
  const text = lines.map((l) => {
    const t = l[target];
    const other: Column = target === "desktop" ? "mobile" : "desktop";
    const o = l[other];
    return `${l.informational ? "(info) " : ""}${l.item}: ${fmt(l.value)} — ${target} ≤ ${fmt(t.limit)} ${mark(t.pass)} · ${other} ≤ ${fmt(o.limit)} ${mark(o.pass)}`;
  });
  text.push(`tier ${b.tier} against the ${target} column: ${mark(pass)}`);
  return { target, pass, lines, text };
}

function round(v: number): number {
  return Math.round(v * 100) / 100;
}

function fmt(v: number): string {
  return v >= 10_000 ? v.toLocaleString("en-GB") : String(v);
}
